import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { EpgChannel } from '@jwp/ott-common/types/epg';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createLiveEventMetadata, createVideoFormatMetadata, createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { isExpired as isExpiredCheck, isPlayable, MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';

import { useEpisodes } from './series/useEpisodes';

export default function useLiveSeriesData(data: PlaylistItem, id: string, episodeIdParam: string | null, feedId: string | null) {
  const { t, i18n } = useTranslation(['video', 'common']);

  // Config
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { features, custom } = config;
  const isFavoritesEnabled: boolean = Boolean(features?.favoritesList);
  const isSharingEnabled: boolean = Boolean(!custom?.disableShare);
  const watchHistory = useWatchHistoryStore((state) => state.getDictionaryWithEpisodes());

  // Media
  const { isLoading: isPlaylistLoading, data: playlist } = usePlaylist(features?.recommendationsPlaylist || '', { related_media_id: id });

  // @CUSTOMER vd-ott-iq this uses the EPG in the background (needs refactor)
  const {
    data: episodes,
    isFetching: isEpisodesFetching,
    hasNextPage: hasNextEpisodesPage,
    fetchNextPage: fetchNextEpisodes,
  } = useEpisodes(data.mediaid, undefined, { enabled: true });
  const episodesPlaylist = useMemo(
    () => ({
      title: data.title,
      description: data.description,
      feedid: data.mediaid,
      playlist: episodes?.flatMap((e) => e.episodes) || [],
    }),
    [data, episodes],
  );

  // Current EPG episode
  const episode = useMemo(() => {
    if (!episodesPlaylist.playlist.length) return;

    if (episodeIdParam) {
      const foundEpisode = episodesPlaylist.playlist.find((item) => item.mediaid === episodeIdParam);
      if (foundEpisode) return foundEpisode;
    }

    // find relevant episode
    const liveEpisode = episodesPlaylist.playlist.find((episode) => episode.mediaStatus === MediaStatus.LIVE);
    const latestVodEpisode = [...episodesPlaylist.playlist].reverse().find((episode) => episode.mediaStatus === MediaStatus.VOD);
    const firstScheduledEpisode = episodesPlaylist.playlist.find((episode) => episode.mediaStatus === MediaStatus.SCHEDULED);

    return liveEpisode || latestVodEpisode || firstScheduledEpisode;
  }, [episodesPlaylist, episodeIdParam]);

  // @CUSTOMER vd-ott-iq stream IQ specific way to fetch channel information
  // @TODO refactor the EPG/Content services to allow fetching channels without using the `useMedia` hook
  const { data: channelItem } = useMedia(episode?.liveChannelsId || '');
  const catchupHours = channelItem?.catchupHours ? Number(channelItem.catchupHours) : 0;
  const isExpired = !!episode && isExpiredCheck(episode, catchupHours);

  // User, entitlement
  const { isEntitled, mediaOffers } = useEntitlement(data);
  const episodesEntitlements = useEntitlement(episodes.flatMap((episode) => episode.episodes));
  const areEpisodesEntitled = episodesEntitlements.reduce((acc, { mediaid, isEntitled }) => {
    return { ...acc, [mediaid!]: isEntitled };
  }, {} as { [mediaid: string]: boolean });
  const { isLoggedIn, hasSubscription } = useAccountStore(({ user, subscription }) => ({ isLoggedIn: !!user, hasSubscription: !!subscription }), shallow);

  // UI
  const getCardUrl = (item: PlaylistItem) => mediaURL({ id: item.mediaid, title: item.title, playlistId: config.features?.recommendationsPlaylist });
  const movieURL = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: false });
  const playUrl = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: true });
  const primaryMetadata = [
    ...createVideoMetadata(
      { ...data, rating: '' },
      {
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
  ];
  const liveEventMetadata = createLiveEventMetadata(episode?.scheduledStart, episode?.scheduledEnd, i18n.language);

  const videoFormats = createVideoFormatMetadata(data);
  const videoRating = [episode?.rating, episode?.ratingComponents].filter(Boolean);

  const channel = channelItem
    ? ({
        id: channelItem.mediaid,
        title: channelItem.title,
        description: channelItem.description,
        backgroundImage: channelItem.backgroundImage,
        channelLogoImage: channelItem.channelLogoImage,
        catchupHours: channelItem.catchupHours ? Number(channelItem.catchupHours) : 0,
      } as EpgChannel)
    : undefined;

  // switch between the live channel and VOD item
  const playingItem = useMemo(() => {
    return (episode?.mediaStatus === MediaStatus.LIVE && channelItem ? channelItem : episode) || data;
  }, [episode, channelItem, data]);

  return {
    isPlayable: episode && isPlayable(episode) && !isExpired,
    playingItem,
    isExpired,
    episodesPlaylist,
    areEpisodesEntitled,
    isEpisodesFetching,
    hasNextEpisodesPage,
    fetchNextEpisodes,
    episode,
    isLoggedIn,
    hasSubscription,
    playlist,
    isPlaylistLoading,
    mediaOffers,
    movieURL,
    playUrl,
    accessModel,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    liveEventMetadata,
    videoFormats,
    videoRating,
    channel,
    watchHistory,
    getCardUrl,
  };
}
